/**
 * Declarative layout for the raw Cash Flow Statement table.
 *
 * Row order is explicit — never inferred from JSON key insertion order.
 * Lookup keys match financialMetricRegistry sourcePath labels exactly.
 */

export const CASH_FLOW_STATEMENT_SECTIONS = [
    {
        id: 'operating',
        totalRowLabel: 'Cash from Operating Activity',
        driverRowLabels: [
            'Profit from operations',
            'Receivables',
            'Inventory',
            'Payables',
            'Loans Advances',
            'Other WC items',
        ],
        subtotalRowLabel: 'Working capital changes',
        trailingDriverRowLabels: ['Direct taxes'],
    },
    {
        id: 'investing',
        totalRowLabel: 'Cash from Investing Activity',
        driverRowLabels: [
            'Fixed assets purchased',
            'Fixed assets sold',
            'Investments purchased',
            'Investments sold',
            'Interest received',
            'Dividends received',
            'Other investing items',
        ],
    },
    {
        id: 'financing',
        totalRowLabel: 'Cash from Financing Activity',
        driverRowLabels: [
            'Proceeds from shares',
            'Proceeds from borrowings',
            'Repayment of borrowings',
            'Interest paid fin',
            'Dividends paid',
            'Financial liabilities',
            'Other financing items',
        ],
    },
];

export const CASH_FLOW_SUMMARY_ROW_LABEL = 'Net Cash Flow';
export const CASH_FLOW_SUPPLEMENTARY_ROW_LABELS = ['Free Cash Flow', 'CFO/OP'];

export const CASH_FLOW_OTHER_ITEMS_LABEL = 'Other items';
export const CASH_FLOW_UNCLASSIFIED_LABEL = 'Unclassified';
export const CASH_FLOW_DERIVED_ANALYSIS_LABEL = 'Derived analysis';

const COLLAPSIBLE_SECTION_IDS = new Set(CASH_FLOW_STATEMENT_SECTIONS.map((section) => section.id));

function collectKnownLabels() {
    const known = new Set();
    CASH_FLOW_STATEMENT_SECTIONS.forEach((section) => {
        known.add(section.totalRowLabel);
        (section.driverRowLabels || []).forEach((label) => known.add(label));
        if (section.subtotalRowLabel) known.add(section.subtotalRowLabel);
        (section.trailingDriverRowLabels || []).forEach((label) => known.add(label));
    });
    known.add(CASH_FLOW_SUMMARY_ROW_LABEL);
    CASH_FLOW_SUPPLEMENTARY_ROW_LABELS.forEach((label) => known.add(label));
    return known;
}

export const CASH_FLOW_KNOWN_ROW_LABELS = collectKnownLabels();

/**
 * Best-effort parent section for an unexpected line item.
 * Ambiguous labels stay unclassified rather than being forced into a section.
 *
 * @param {string} label
 * @returns {'operating' | 'investing' | 'financing' | 'unclassified'}
 */
export function inferUnmappedSection(label) {
    const text = String(label || '').toLowerCase();
    if (!text.trim()) return 'unclassified';

    if (/dividend\s*paid|interest\s*paid/.test(text)) return 'financing';
    if (/borrow|repay|financ/.test(text)) return 'financing';
    if (/\bshares?\b/.test(text)) return 'financing';

    if (/fixed\s*asset|capex|\binvest/.test(text)) return 'investing';

    if (/receivable|inventor|payable|working\s*capital|direct\s*tax|profit\s*from/.test(text)) {
        return 'operating';
    }

    return 'unclassified';
}

function hasRow(data, label) {
    return Boolean(data) && Object.prototype.hasOwnProperty.call(data, label);
}

function pushDataRow(rows, data, label, role, sectionId) {
    if (!hasRow(data, label)) return;
    rows.push({ kind: 'data', label, role, sectionId: sectionId || null });
}

/**
 * Flatten cash-flow JSON into presentation rows.
 * Missing mapped labels are omitted. Unmapped labels are never dropped.
 *
 * @param {Record<string, unknown>|null|undefined} cashFlow
 * @returns {Array<{
 *   kind: 'data' | 'groupLabel' | 'divider',
 *   label: string,
 *   role: 'total' | 'driver' | 'subtotal' | 'other' | 'grandTotal' | 'supplementary' | 'groupLabel' | 'divider',
 *   sectionId: string|null,
 * }>}
 */
export function buildCashFlowStatementRows(cashFlow) {
    const data = cashFlow && typeof cashFlow === 'object' ? cashFlow : {};
    const unmappedBySection = {
        operating: [],
        investing: [],
        financing: [],
        unclassified: [],
    };

    Object.keys(data).forEach((label) => {
        if (CASH_FLOW_KNOWN_ROW_LABELS.has(label)) return;
        const sectionId = inferUnmappedSection(label);
        unmappedBySection[sectionId].push(label);
    });

    const rows = [];

    CASH_FLOW_STATEMENT_SECTIONS.forEach((section) => {
        pushDataRow(rows, data, section.totalRowLabel, 'total', section.id);
        (section.driverRowLabels || []).forEach((label) => {
            pushDataRow(rows, data, label, 'driver', section.id);
        });
        if (section.subtotalRowLabel) {
            pushDataRow(rows, data, section.subtotalRowLabel, 'subtotal', section.id);
        }
        (section.trailingDriverRowLabels || []).forEach((label) => {
            pushDataRow(rows, data, label, 'driver', section.id);
        });

        const extras = unmappedBySection[section.id];
        if (extras.length) {
            rows.push({
                kind: 'groupLabel',
                label: CASH_FLOW_OTHER_ITEMS_LABEL,
                role: 'groupLabel',
                sectionId: section.id,
            });
            extras.forEach((label) => {
                rows.push({ kind: 'data', label, role: 'other', sectionId: section.id });
            });
        }
    });

    pushDataRow(rows, data, CASH_FLOW_SUMMARY_ROW_LABEL, 'grandTotal', null);

    const supplementaryPresent = CASH_FLOW_SUPPLEMENTARY_ROW_LABELS.filter((label) => hasRow(data, label));
    if (supplementaryPresent.length) {
        rows.push({
            kind: 'divider',
            label: CASH_FLOW_DERIVED_ANALYSIS_LABEL,
            role: 'divider',
            sectionId: null,
        });
        supplementaryPresent.forEach((label) => {
            rows.push({ kind: 'data', label, role: 'supplementary', sectionId: null });
        });
    }

    if (unmappedBySection.unclassified.length) {
        rows.push({
            kind: 'groupLabel',
            label: CASH_FLOW_UNCLASSIFIED_LABEL,
            role: 'groupLabel',
            sectionId: null,
        });
        unmappedBySection.unclassified.forEach((label) => {
            rows.push({ kind: 'data', label, role: 'other', sectionId: null });
        });
    }

    return rows;
}

export function isCashFlowSectionCollapsible(sectionId) {
    return COLLAPSIBLE_SECTION_IDS.has(sectionId);
}

/**
 * Whether a layout row should render given the section expand map.
 * Section totals stay visible when collapsed; their nested rows hide.
 *
 * @param {{ role: string, sectionId: string|null }} row
 * @param {Record<string, boolean>} expanded
 */
export function isCashFlowStatementRowVisible(row, expanded) {
    if (!row) return false;
    if (row.role === 'total' || row.role === 'grandTotal' || row.role === 'supplementary' || row.role === 'divider') {
        return true;
    }
    if (row.sectionId && isCashFlowSectionCollapsible(row.sectionId)) {
        return expanded?.[row.sectionId] !== false;
    }
    return true;
}
